import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LayoutGrid, Store, Plus, MessageCircle, User } from 'lucide-react';


/**
 * Lucide `Store` wrapped with `strokeLinecap="butt"` so the three paths that
 * meet at (2,7) and (22,7) don't stack their round half-disc caps into a
 * bright spot. Same silhouette as the original, no corner flare.
 */
const StorefrontIcon = ({ className, strokeWidth = 2 }: { className?: string; strokeWidth?: number }) => (
  <Store
    className={className}
    strokeWidth={strokeWidth}
    strokeLinecap="butt"
    strokeLinejoin="miter"
  />
);
import { cn } from '@/lib/utils';
import { haptics } from '@/lib/haptics';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useEarlyRole } from '@/hooks/useEarlyRole';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
// useUIStore moved into QuickCreateSheet
import { BottomNavMeSheet } from './BottomNavMeSheet';
import { QuickCreateSheet } from './inventory/QuickCreateSheet';
import { useChatStore } from '@/store/chatStore';
import { useInboxUnreadCounters } from '@/hooks/useInboxUnreadCounters';
import { PrefetchLink } from './PrefetchLink';
import { routePrefetch, RoutePrefetchKey } from '@/lib/routePrefetch';
import { transformedImageUrl } from '@/lib/imageTransform';
import { RETAIL_MODE } from '@/lib/retailMode';

const HIDDEN_ROUTES = [
  '/auth',
  '/auth/callback',
  '/logo-export',
  '/install',
  '/offer',
  '/safety',
  '/privacy',
  '/terms',
];


interface NavItem {
  to?: string;
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  active: (path: string) => boolean;
  onClick?: () => void;
  raised?: boolean;
  /** Unread count rendered as a gold dot/badge on the icon. */
  badge?: number;
  /** Render greyed-out, non-navigating placeholder for a coming-soon slot. */
  disabled?: boolean;
  /** Lazy chunk to pre-warm on touchstart/hover. */
  prefetch?: RoutePrefetchKey;
}

export function BottomNav() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();
  const { isSeller: isEarlySeller, isBuyer: isEarlyBuyer } = useEarlyRole();
  // navigate handled inside QuickCreateSheet
  const [meOpen, setMeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const openChat = useChatStore((s) => s.open);
  const chatOpen = useChatStore((s) => s.isOpen);
  const { total: inboxUnread } = useInboxUnreadCounters();

  if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  const username = profile?.username;
  const path = location.pathname;
  const isGuest = !user;
  // During early access, buyers see a stripped nav; sellers get the full app.
  const earlyAccessLocked = isEarlyBuyer;

  // Hide on marketing landing (signed-out EarlyAccess) and buyer confirmation page.
  // These are CTA-driven surfaces with no real app to navigate yet.
  if (path === '/' && (isGuest || isEarlyBuyer)) return null;

  const initials = (username || 'me').slice(0, 2).toUpperCase();

  const allItems: NavItem[] = [
    {
      to: isGuest ? '/auth' : '/inventory',
      label: 'Inventory',
      icon: LayoutGrid,
      active: (p) => p === '/inventory',
      prefetch: 'inventory',
    },
    {
      to: isGuest ? '/auth' : '/preview',
      label: 'Store',
      icon: StorefrontIcon,
      active: (p) =>
        p === '/preview' ||
        (!!username && (p === `/u/${username}` || p.startsWith(`/u/${username}/`))) ||
        p.startsWith('/item/'),
      prefetch: 'publicProfile',
    },
    {
      label: 'Create',
      icon: Plus,
      raised: true,
      active: () => false,
      onClick: () => {
        haptics.impact();
        if (isGuest) {
          window.location.href = '/auth';
        } else {
          setCreateOpen(true);
        }
      },
    },
    {
      label: 'Messages',
      icon: MessageCircle,
      badge: isGuest ? 0 : inboxUnread,
      active: () => chatOpen,
      onClick: () => {
        haptics.tap();
        if (isGuest) {
          window.location.href = '/auth';
        } else {
          openChat();
        }
      },
    } as NavItem,

    {
      label: isGuest ? 'Sign In' : 'Me',
      icon: User,
      active: (p) =>
        ['/profile', '/settings', '/transactions', '/admin', '/contact'].includes(p),
      onClick: () => {
        haptics.tap();
        if (isGuest) {
          window.location.href = '/auth';
        } else {
          setMeOpen(true);
        }
      },
    },
  ].filter(Boolean) as NavItem[];

  const items = earlyAccessLocked
    ? allItems.filter((i) => i.label === 'Inventory' || i.label === 'Me')
    : allItems;

  return (
    <>
      <nav
        aria-label="Primary"
        className={cn(
          'md:hidden fixed inset-x-3 z-40 rounded-full animate-fade-in',
          'glass-obsidian',
        )}
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + var(--bottom-nav-gap, 10px))' }}
      >


        <ul className="relative flex items-stretch justify-around h-[52px] px-2">
          {items.map((item, idx) => {
            const Icon = item.icon;
            const isActive = item.active(path);
            const isUserSlot = item.label === 'Me' || item.label === 'Sign In';

            let content: React.ReactNode;

            if (item.raised) {
              // Slim pill "+" — sandwiched inside the bar, not a circle.
              content = (
                <div className="flex items-center justify-center h-full">
                  <div
                    className={cn(
                      'relative flex items-center justify-center h-7 px-4 rounded-full',
                      'bg-active-gold text-black',
                      'shadow-[inset_0_1px_0_hsl(50,100%,88%),inset_0_-1px_0_rgba(0,0,0,0.12)]',
                      'transition-transform duration-200 ease-out active:scale-90',
                    )}
                  >
                    <Icon className="w-[18px] h-[18px]" strokeWidth={2.75} />
                  </div>
                </div>
              );
            } else if (isUserSlot && !isGuest) {
              content = (
                <div className="flex items-center justify-center h-full">
                  <div
                    className={cn(
                      'relative w-6 h-6 rounded-full transition-all duration-200',
                    )}
                  >
                    <Avatar className="w-6 h-6">
                      <AvatarImage
                        src={transformedImageUrl(profile?.avatar_url, { width: 96, height: 96, quality: 75 }) || profile?.avatar_url || undefined}
                        alt={username || 'Profile'}
                        className="object-cover object-center"
                        onError={(e) => {
                          const t = e.currentTarget;
                          if (profile?.avatar_url && t.src !== profile.avatar_url) t.src = profile.avatar_url;
                        }}
                      />
                      <AvatarFallback className="text-[9px] font-semibold bg-white/10 text-foreground/80">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>
              );
            } else {
              content = (
                <div className="relative flex items-center justify-center h-full w-full select-none">
                  <div
                    className={cn(
                      'relative flex items-center justify-center h-8 w-8 transition-colors duration-200 ease-out',
                      item.disabled
                        ? 'text-foreground/25'
                        : isActive ? 'text-active-gold' : 'text-foreground/55',
                    )}
                  >
                    <Icon
                      className="w-[22px] h-[22px] shrink-0"
                      strokeWidth={2}
                    />
                    {!!item.badge && item.badge > 0 && (
                      <span
                        aria-label={`${item.badge} unread`}
                        className={cn(
                          'absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1',
                          'flex items-center justify-center rounded-full',
                          'bg-active-gold text-black text-[10px] font-bold leading-none',
                          'ring-2 ring-[#121416]',
                        )}
                      >
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <li key={idx} className="flex-1 min-w-0">
                {item.to && !item.onClick && !item.disabled ? (
                  <PrefetchLink
                    to={item.to}
                    prefetch={item.prefetch ? routePrefetch[item.prefetch] : undefined}
                    className="block h-full active:scale-[0.94] transition-transform duration-150"
                    aria-label={item.label}
                    onClick={() => haptics.tap()}
                  >
                    {content}
                  </PrefetchLink>
                ) : (
                  <button
                    type="button"
                    onClick={item.disabled ? undefined : item.onClick}
                    disabled={item.disabled}
                    aria-disabled={item.disabled}
                    aria-label={item.disabled ? `${item.label} (coming soon)` : item.label}
                    className={cn(
                      'block h-full w-full transition-transform duration-150',
                      item.disabled ? 'cursor-not-allowed' : 'active:scale-[0.94]',
                    )}
                  >
                    {content}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>


      <BottomNavMeSheet open={meOpen} onOpenChange={setMeOpen} />
      <QuickCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
